import { derived, writable } from "svelte/store";
import { isGameChatMessageTO, isGameMessageTO, isGameNotificationTO, isGameTypesTO, isLobbyChatMessageTO, isOpenGameListTO, isSettingsTO, isSystemNotificationTO, isUsersOnlineTO } from "../dto";
const loadPersistedData = () => {
    const data = JSON.parse((localStorage === null || localStorage === void 0 ? void 0 : localStorage.getItem('cardodrom-login')) || null);
    if (!data || !('username' in data && 'hostname' in data && 'port' in data && 'loggedIn' in data)) {
        localStorage === null || localStorage === void 0 ? void 0 : localStorage.removeItem('cardodrom-login');
        return null;
    }
    const { username, hostname, port, loggedIn } = data;
    return { username, hostname, port, loggedIn };
};
const persistData = (subs) => {
    subs.username.subscribe(username => subs.hostname.subscribe(hostname => subs.port.subscribe(port => subs.loggedIn.subscribe(loggedIn => {
        localStorage === null || localStorage === void 0 ? void 0 : localStorage.setItem('cardodrom-login', JSON.stringify({ username, hostname, port, loggedIn }));
    }))));
};
const createConnectionStore = () => {
    let websocket;
    const dtoEmitter = new EventTarget();
    const addListener = (event) => (listener) => dtoEmitter.addEventListener(event, listener);
    const dispatchMessage = (event, dto) => dtoEmitter.dispatchEvent(new CustomEvent(event, { detail: dto }));
    const persistedData = loadPersistedData();
    // fields - username, port, hostname
    const username = writable((persistedData === null || persistedData === void 0 ? void 0 : persistedData.username) || "");
    const hostname = writable((persistedData === null || persistedData === void 0 ? void 0 : persistedData.hostname) || "ghci.de");
    const port = writable((persistedData === null || persistedData === void 0 ? void 0 : persistedData.port) || 1106);
    const loggedIn = writable(false);
    const isLoggingIn = writable(false);
    const pingCount = writable(0);
    const loggedInUsername = derived([loggedIn, username], ([loggedIn, username]) => loggedIn ? username : null);
    const handleDto = (msg) => {
        let dto;
        try {
            dto = JSON.parse(msg.data);
        }
        catch (err) {
            console.error('[PARSE ERROR] could not parse', msg.data, msg);
            return;
        }
        if (isSystemNotificationTO(dto)) {
            switch (dto.notification) {
                case "ALIVE":
                    pingCount.update((n) => n + 1);
                    send({ notification: "ALIVE" });
                    break;
                case "LOGIN_SUCCESSFUL":
                    isLoggingIn.set(false);
                    loggedIn.set(true);
                    persistData({ username, hostname, port, loggedIn });
                    break;
                case "LOGIN_FAILED":
                    dispatchMessage('login-error', { reason: dto.notification });
                    disconnect();
                    break;
                case "LOGIN_USERNAME_EXISTS":
                    dispatchMessage('login-error', { reason: dto.notification });
                    disconnect();
                    break;
                case "LOGIN_USERNAME_INVALID":
                    dispatchMessage('login-error', { reason: dto.notification });
                    disconnect();
                    break;
                default:
                    console.log("unhandled SystemNotification", dto);
            }
        }
        else if (isLobbyChatMessageTO(dto)) {
            dispatchMessage('lobby-chat-message', dto);
        }
        else if (isGameTypesTO(dto)) {
            dispatchMessage('game-types', dto);
        }
        else if (isUsersOnlineTO(dto)) {
            dispatchMessage('users-online', dto);
        }
        else if (isOpenGameListTO(dto)) {
            dispatchMessage('open-game-list', dto);
        }
        else if (isSettingsTO(dto)) {
            dispatchMessage('settings', dto);
        }
        else if (isGameNotificationTO(dto)) {
            dispatchMessage('game-notification', dto);
        }
        else if (isGameChatMessageTO(dto)) {
            dispatchMessage('game-chat-message', dto);
        }
        else if (isGameMessageTO(dto)) {
            dispatchMessage('game-message', dto);
        }
        else {
            console.log("unhandled dto", dto);
        }
    };
    const disconnect = () => {
        isLoggingIn.set(false);
        loggedIn.set(false);
        if (websocket) {
            websocket.onopen = null;
            websocket.onclose = null;
            websocket.onmessage = null;
            websocket.onerror = null;
            send({ notification: 'LOGOUT' });
            websocket.close();
            websocket = null;
        }
        // persistData({username, hostname, port, loggedIn})
    };
    const logout = () => {
        disconnect();
        persistData({ username, hostname, port, loggedIn });
    };
    const connect = (args) => {
        username.set(args.username);
        hostname.set(args.hostname);
        port.set(args.port);
        if (websocket) {
            return;
        }
        isLoggingIn.set(true);
        // establish websocket
        const wsProtocol = (location.protocol === 'https:' || args.port === 443) ? 'wss:' : 'ws:';
        const needsPortSuffix = wsProtocol === 'ws:' && args.port !== 80 || wsProtocol === 'wss:' && args.port !== 443;
        const portSuffix = needsPortSuffix ? `:${args.port}` : '';
        websocket = new WebSocket(`${wsProtocol}//${args.hostname}${portSuffix}`);
        websocket.onopen = () => send({ clientName: args.username, version: 3 });
        websocket.onmessage = (msg) => handleDto(msg);
        websocket.onclose = () => disconnect();
        websocket.onerror = (event) => {
            console.error(`[WS ERROR]`, event);
            disconnect();
        };
    };
    const send = (dto) => {
        if (!websocket) {
            return;
        }
        websocket.send(JSON.stringify(dto));
    };
    if (persistedData === null || persistedData === void 0 ? void 0 : persistedData.loggedIn) {
        connect({
            username: persistedData.username,
            hostname: persistedData.hostname,
            port: persistedData.port
        });
    }
    return {
        connect,
        send,
        logout,
        username: { subscribe: username.subscribe },
        hostname: { subscribe: hostname.subscribe },
        port: { subscribe: port.subscribe },
        loggedIn: { subscribe: loggedIn.subscribe },
        isLoggingIn: { subscribe: isLoggingIn.subscribe },
        loggedInUsername: { subscribe: loggedInUsername.subscribe },
        pingCount: { subscribe: pingCount.subscribe },
        onLobbyChatMessage: addListener('lobby-chat-message'),
        onGameTypes: addListener('game-types'),
        onUsersOnline: addListener('users-online'),
        onOpenGameList: addListener('open-game-list'),
        onLoginError: addListener('login-error'),
        onSettings: addListener('settings'),
        onGameNotification: addListener('game-notification'),
        onGameMessage: addListener('game-message'),
        onGameChatMessage: addListener('game-chat-message'),
    };
};
const store = createConnectionStore();
export default store;
